<div class="bk-page det">
  @if (isLoading) {
    <div class="det__loading" role="status" aria-busy="true">
      <span class="bk-sr-only">Loading application</span>
      <span class="bk-skel" style="width: 120px; height: 12px" aria-hidden="true"></span>
      <span class="bk-skel" style="width: min(420px, 80%); height: 34px" aria-hidden="true"></span>
      <span class="bk-skel" style="width: min(300px, 60%); height: 14px" aria-hidden="true"></span>
      <span class="bk-skel det__loading-panel" aria-hidden="true"></span>
      <span class="bk-skel det__loading-panel det__loading-panel--tall" aria-hidden="true"></span>
    </div>
  } @else if (app) {
    <header class="bk-page__head">
      <div class="bk-page__intro">
        <button type="button" class="bk-more det__back" (click)="goBack()">
          <span class="bk-sym" aria-hidden="true">arrow_back</span>
          Applications
        </button>
        <p class="bk-eyebrow">USSD application</p>
        <h1 class="bk-page__title">{{ app.name }}</h1>
        <ul class="det__meta" aria-label="Summary">
          <li><app-status [text]="status.text" [status]="status.status" /></li>
          <li class="det__code">{{ app.serviceCode || 'No code' }}</li>
          <li>{{ country }}</li>
          <li>{{ environmentLabel(app.environment) }}</li>
        </ul>
      </div>

      <div class="bk-page__actions">
        <button
          type="button"
          class="bk-btn bk-btn--ghost"
          [disabled]="!app.serviceCode"
          (click)="testInSimulator()"
        >
          <span class="bk-sym" aria-hidden="true">dialpad</span>
          Test in simulator
        </button>
        <button type="button" class="bk-btn bk-btn--ghost" (click)="goToEdit()">
          <span class="bk-sym" aria-hidden="true">edit</span>
          Edit
        </button>
        <button
          type="button"
          class="bk-btn bk-btn--ghost"
          [disabled]="isTogglingStatus"
          [attr.aria-busy]="isTogglingStatus || null"
          (click)="toggleStatus()"
        >
          <span class="bk-sym" aria-hidden="true">{{ isEnabled ? 'pause_circle' : 'play_circle' }}</span>
          {{ isTogglingStatus ? (isEnabled ? 'Disabling…' : 'Enabling…') : isEnabled ? 'Disable' : 'Enable' }}
        </button>
        @if (canPromote) {
          <button type="button" class="bk-btn bk-btn--primary" (click)="openPromoteModal()">
            <span class="bk-sym" aria-hidden="true">upgrade</span>
            {{ promoteButtonTitle }}
          </button>
        }
      </div>
    </header>

    <!-- ── Where the application stands ── -->
    <section class="bk-panel det__track" aria-labelledby="det-track-title">
      <h2 class="bk-sr-only" id="det-track-title">Environment</h2>
      <ol class="track">
        @for (stage of ENV_STAGES; track stage.key; let i = $index) {
          <li class="track__stage" [attr.data-state]="stageState(i)" [attr.aria-current]="stageState(i) === 'current' ? 'step' : null">
            <span class="track__node" aria-hidden="true">
              @if (stageState(i) === 'done') {
                <span class="bk-sym">check</span>
              } @else if (stageState(i) === 'pending') {
                <span class="bk-sym">hourglass_top</span>
              } @else {
                {{ i + 1 }}
              }
            </span>
            <span class="track__text">
              <span class="track__label">{{ stage.label }}</span>
              @switch (stageState(i)) {
                @case ('current') { <span class="track__note">Current</span> }
                @case ('done') { <span class="track__note">Passed</span> }
                @case ('pending') { <span class="track__note">Awaiting approval</span> }
              }
            </span>
          </li>
        }
      </ol>

      <div class="det__track-foot">
        @if (pendingPromotion; as pending) {
          <p class="det__track-line">
            <app-status text="Awaiting approval" status="pending" />
            <span>
              {{ environmentLabel(pending.targetEnvironment) }} requested
              @if (pending.requestedBy) { by {{ pending.requestedBy }} }
              @if (pending.requestedAt) { {{ formatWhen(pending.requestedAt) }} }.
              It stays in {{ environmentLabel(app.environment) }} until a reviewer approves it.
            </span>
          </p>
        } @else if (!nextStage) {
          <p class="det__track-line det__track-line--quiet">In production. There is no further stage.</p>
        } @else {
          <p class="det__track-line det__track-line--quiet">
            Next stage: {{ environmentLabel(nextStage) }}{{ isPromotingToProduction ? ', after approval' : '' }}.
          </p>
        }
        @if (app.environmentAutoPromotion) {
          <p class="det__track-line det__track-line--quiet">
            <span class="bk-sym" aria-hidden="true">auto_mode</span>
            Auto-promotion is on.
          </p>
        }
      </div>
    </section>

    <app-routing-tab class="det__tabs" [tabList]="tabs" />

    @switch (activeTab) {
      <!-- ══════════ ROUTING ══════════ -->
      @case ('routing') {
        <section class="det__section" aria-labelledby="det-routing-title">
          <div class="det__section-head">
            <div>
              <h2 class="bk-section-title" id="det-routing-title">Routing</h2>
              <p class="det__section-sub">Where requests for {{ app.serviceCode }} go, per network and sub-code.</p>
            </div>
          </div>

          <div class="bk-panel det__table-panel">
            @if (routing.length) {
              <div class="det__scroll" tabindex="0" role="region" aria-label="Routing rules">
                <table class="bk-log det__routing">
                  <thead>
                    <tr>
                      <th scope="col">Network</th>
                      <th scope="col">Sub-code</th>
                      <th scope="col" class="col-target">Target</th>
                      <th scope="col" class="num col-version">Version</th>
                      <th scope="col" class="col-pass">Passthrough</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (cfg of routing; track cfg.id) {
                      <tr>
                        <td>
                          <span class="bk-msg">
                            <span class="bk-msg__subject">{{ cfg.mno || 'Any network' }}</span>
                            @if (cfg.isDefault) {
                              <span class="bk-msg__preview">Default route</span>
                            }
                          </span>
                        </td>
                        <td class="quiet">{{ cfg.serviceSubCode || '—' }}</td>
                        <td class="col-target">
                          <span class="det__url">{{ cfg.targetUrl || cfg.targetApplication || '—' }}</span>
                        </td>
                        <td class="num quiet col-version">{{ cfg.version ?? '—' }}</td>
                        <td class="quiet col-pass">{{ cfg.passThrough ? 'On' : 'Off' }}</td>
                        <td>
                          <app-status
                            [text]="cfg.isActive ? 'Active' : 'Inactive'"
                            [status]="cfg.isActive ? 'success' : 'inactive'"
                          />
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <app-empty-state
                icon="alt_route"
                message="No routing rules"
                hint="Every request for this code goes to the callback URL in Configuration."
                size="small"
              />
            }
          </div>
        </section>
      }

      <!-- ══════════ SESSIONS ══════════ -->
      @case ('sessions') {
        <section class="det__section" aria-labelledby="det-sessions-title">
          <div class="det__section-head">
            <div>
              <h2 class="bk-section-title" id="det-sessions-title">Sessions</h2>
              <p class="det__section-sub">Subscriber sessions on {{ app.serviceCode }} and how they ended.</p>
            </div>
            <div class="det__section-actions">
              <div class="det__seg" role="group" aria-label="Time window">
                @for (w of sessionWindows; track w.days) {
                  <button
                    type="button"
                    class="det__seg-btn"
                    [class.is-on]="sessionWindowDays === w.days"
                    [attr.aria-pressed]="sessionWindowDays === w.days"
                    [disabled]="isLoadingSessions"
                    (click)="setSessionWindow(w.days)"
                  >
                    {{ w.label }}
                  </button>
                }
              </div>
              <button
                type="button"
                class="bk-btn bk-btn--ghost"
                [disabled]="!sessions.length"
                (click)="downloadSessions()"
              >
                <span class="bk-sym" aria-hidden="true">download</span>
                Download CSV
              </button>
            </div>
          </div>

          @if (sessionsErrorMsg) {
            <div class="bk-panel det__error" role="alert">
              <span class="det__error-dot" aria-hidden="true"></span>
              <p>{{ sessionsErrorMsg }}</p>
              <button type="button" class="bk-btn bk-btn--ghost" (click)="loadSessions()">Try again</button>
            </div>
          } @else if (isLoadingSessions) {
            <div class="bk-stats det__stats" aria-hidden="true">
              @for (n of [1, 2, 3, 4]; track n) {
                <div class="bk-stat"><span class="bk-skel" style="height: 26px; width: 60%"></span><span class="bk-skel" style="height: 12px; width: 80%; margin-top: 8px"></span></div>
              }
            </div>
          } @else if (breakdown) {
            @if (breakdown; as b) {
            <div class="bk-stats det__stats">
              <div class="bk-stat">
                <div class="bk-stat__value">{{ formatCount(b.total) }}</div>
                <div class="bk-stat__label">{{ plural(b.total, 'session', 'sessions') }}</div>
              </div>
              <div class="bk-stat" data-tone="ok">
                <div class="bk-stat__value">{{ share(b.completed) }}</div>
                <div class="bk-stat__label">Completed ({{ formatCount(b.completed) }})</div>
              </div>
              <div class="bk-stat">
                <div class="bk-stat__value">{{ share(b.user) }}</div>
                <div class="bk-stat__label">Ended by subscriber ({{ formatCount(b.user) }})</div>
              </div>
              <div class="bk-stat" [attr.data-tone]="b.failed ? 'bad' : null">
                <div class="bk-stat__value">{{ share(b.failed) }}</div>
                <div class="bk-stat__label">Failed or timed out ({{ formatCount(b.failed) }})</div>
              </div>
              <div class="bk-stat">
                <div class="bk-stat__value">{{ b.averageDuration }}</div>
                <div class="bk-stat__label">Average length, {{ b.averageHops }} steps</div>
              </div>
            </div>
            @if (b.failures.length) {
              <ul class="det__failures" aria-label="Failure reasons">
                @for (f of b.failures; track f.label) {
                  <li><app-status [text]="f.label" status="failed" /> <strong>{{ formatCount(f.count) }}</strong></li>
                }
                @if (b.open) {
                  <li><app-status text="In progress" status="active" /> <strong>{{ formatCount(b.open) }}</strong></li>
                }
              </ul>
            }
            }
          }

          @if (!sessionsErrorMsg) {
            <div class="bk-panel det__table-panel det__sessions">
              @if (isLoadingSessions) {
                <div class="det__rows-skel" aria-hidden="true">
                  @for (n of [1, 2, 3, 4, 5]; track n) {
                    <span class="bk-skel" style="height: 18px"></span>
                  }
                </div>
              } @else if (sessionRows.length) {
                <div class="det__scroll" tabindex="0" role="region" aria-label="Sessions">
                  <table class="bk-log">
                    <thead>
                      <tr>
                        <th scope="col">Started</th>
                        <th scope="col">Number</th>
                        <th scope="col" class="col-mno">Network</th>
                        <th scope="col" class="num col-dur">Length</th>
                        <th scope="col" class="num col-hops">Steps</th>
                        <th scope="col">Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (row of sessionRows; track row.key) {
                        <tr>
                          <td class="quiet">{{ row.started }}</td>
                          <td>{{ row.msisdn }}</td>
                          <td class="quiet col-mno">{{ row.mno }}</td>
                          <td class="num quiet col-dur">{{ row.duration }}</td>
                          <td class="num quiet col-hops">{{ row.hops }}</td>
                          <td><app-status [text]="row.outcome.text" [status]="row.outcome.status" /></td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                @if (sessions.length > sessionRows.length) {
                  <p class="det__table-note">
                    Showing the latest {{ formatCount(sessionRows.length) }} of {{ formatCount(sessions.length) }}. Download the CSV for all of them.
                  </p>
                }
              } @else {
                <app-empty-state
                  icon="history"
                  message="No sessions in this window"
                  [hint]="'Sessions show here once subscribers dial ' + (app.serviceCode || 'this code') + '. Simulator sessions may not be recorded.'"
                  size="small"
                />
              }
            </div>
          }
        </section>
      }

      <!-- ══════════ PROMOTIONS ══════════ -->
      @case ('promotions') {
        <section class="det__section" aria-labelledby="det-promo-title">
          <div class="det__section-head">
            <div>
              <h2 class="bk-section-title" id="det-promo-title">Promotions</h2>
              <p class="det__section-sub">Every move between environments, newest first.</p>
            </div>
          </div>

          @if (promotionHistoryErrorMsg) {
            <div class="bk-panel det__error" role="alert">
              <span class="det__error-dot" aria-hidden="true"></span>
              <p>{{ promotionHistoryErrorMsg }}</p>
              <button type="button" class="bk-btn bk-btn--ghost" (click)="loadPromotionHistory()">Try again</button>
            </div>
          } @else {
            <div class="bk-panel det__promos">
              @if (isLoadingPromotions && !promotionHistory.length) {
                <div class="det__rows-skel" aria-hidden="true">
                  @for (n of [1, 2, 3]; track n) {
                    <span class="bk-skel" style="height: 44px"></span>
                  }
                </div>
              } @else if (sortedPromotions.length) {
                <ol class="promos">
                  @for (record of sortedPromotions; track $index) {
                    <li class="promo">
                      <div class="promo__top">
                        <p class="promo__change">
                          {{ environmentLabel(record.sourceEnvironment) }}
                          <span class="bk-sym" aria-hidden="true">arrow_forward</span><span class="bk-sr-only">to</span>
                          {{ environmentLabel(record.targetEnvironment) }}
                          @if (record.version) {
                            <span class="promo__version">v{{ record.version }}</span>
                          }
                        </p>
                        <app-status
                          [text]="promotionStatusView(record.status).text"
                          [status]="promotionStatusView(record.status).status"
                        />
                      </div>
                      <p class="promo__facts">
                        <span>
                          Requested
                          @if (record.requestedBy) { by {{ record.requestedBy }} }
                          @if (record.requestedAt || record.promotedAt) {
                            {{ formatWhen(record.requestedAt || record.promotedAt) }}
                          }
                        </span>
                        @if (record.reviewedBy || record.reviewedAt) {
                          <span>
                            Reviewed
                            @if (record.reviewedBy) { by {{ record.reviewedBy }} }
                            @if (record.reviewedAt) { {{ formatWhen(record.reviewedAt) }} }
                          </span>
                        }
                        @if (record.promotedAt && record.requestedAt) {
                          <span>Promoted {{ formatWhen(record.promotedAt) }}</span>
                        }
                      </p>
                      @if (record.promotionNotes) {
                        <p class="promo__notes">{{ record.promotionNotes }}</p>
                      }
                    </li>
                  }
                </ol>
              } @else {
                <app-empty-state
                  icon="upgrade"
                  message="No promotions yet"
                  [hint]="canPromote ? 'Promote the application when it is ready for ' + environmentLabel(nextStage) + '.' : 'This application has not moved between environments.'"
                  size="small"
                />
              }
            </div>
          }
        </section>
      }

      <!-- ══════════ CONFIGURATION ══════════ -->
      @default {
        <div class="det__grid">
          <section class="bk-panel det__card" aria-labelledby="det-basics">
            <h2 class="bk-eyebrow det__card-title" id="det-basics">Application</h2>
            <dl class="bk-kv">
              <dt>Name</dt>
              <dd>{{ app.name }}</dd>
              <dt>Alias</dt>
              <dd>{{ app.alias || '—' }}</dd>
              <dt>Service code</dt>
              <dd><span class="det__code">{{ app.serviceCode || '—' }}</span></dd>
              <dt>Country</dt>
              <dd>
                {{ country }}
                @if (app.countryCode) { <span class="det__quiet">({{ app.countryCode }}{{ app.internationalCode ? ', ' + app.internationalCode : '' }})</span> }
              </dd>
              <dt>Environment</dt>
              <dd>{{ environmentLabel(app.environment) }}</dd>
              @if (app.description) {
                <dt>Description</dt>
                <dd>{{ app.description }}</dd>
              }
              <dt>Created</dt>
              <dd>{{ app.createdAt ? (app.createdAt | date: 'd MMM y, HH:mm') : '—' }}</dd>
              <dt>Last changed</dt>
              <dd>{{ app.modifiedAt ? (app.modifiedAt | date: 'd MMM y, HH:mm') : '—' }}</dd>
              @if (app.uuid) {
                <dt>ID</dt>
                <dd class="det__quiet">{{ app.uuid }}</dd>
              }
            </dl>
          </section>

          <section class="bk-panel det__card" aria-labelledby="det-callback">
            <div class="det__card-head">
              <h2 class="bk-eyebrow det__card-title" id="det-callback">Callback</h2>
              <button type="button" class="bk-more" aria-label="Change callback settings" (click)="goToEdit()">
                Change
                <span class="bk-sym" aria-hidden="true">arrow_outward</span>
              </button>
            </div>
            <dl class="bk-kv">
              <dt>URL</dt>
              <dd><span class="det__url">{{ app.callbackUrl1 || '—' }}</span></dd>
              @if (app.callbackUrl2) {
                <dt>Fallback URL</dt>
                <dd><span class="det__url">{{ app.callbackUrl2 }}</span></dd>
              }
              <dt>Method</dt>
              <dd>{{ app.callbackMethod || '—' }}</dd>
              <dt>Content type</dt>
              <dd>{{ app.callbackContentType || '—' }}</dd>
              @if (app.callbackServiceName) {
                <dt>Service name</dt>
                <dd>{{ app.callbackServiceName }}</dd>
              }
              <dt>Authentication</dt>
              <dd>{{ authTypeLabel }}</dd>
              @switch (authType) {
                @case ('BEARER') {
                  <dt>Token</dt>
                  <dd class="det__secret">{{ secret(app.callbackBearerToken) }}</dd>
                }
                @case ('BASIC') {
                  <dt>Username</dt>
                  <dd>{{ app.callbackBasicUsername || 'Not set' }}</dd>
                  <dt>Password</dt>
                  <dd class="det__secret">{{ secret(app.callbackBasicPassword) }}</dd>
                }
                @case ('API_KEY') {
                  <dt>Header</dt>
                  <dd>{{ app.callbackApiKeyHeader || 'Not set' }}</dd>
                  <dt>Key</dt>
                  <dd class="det__secret">{{ secret(app.callbackApiKey) }}</dd>
                }
                @case ('CUSTOM') {
                  <dt>Header</dt>
                  <dd>{{ app.callbackCustomAuthHeader || 'Not set' }}</dd>
                  <dt>Value</dt>
                  <dd class="det__secret">{{ secret(app.callbackCustomAuthValue) }}</dd>
                }
              }
            </dl>
            @if (authIsLegacy) {
              <p class="det__note" role="note">
                <span class="det__note-dot" aria-hidden="true"></span>
                The gateway does not support this method, so callbacks go out without credentials.
                Edit the application to pick a supported one.
              </p>
            }
          </section>

          <section class="bk-panel det__card" aria-labelledby="det-flags">
            <h2 class="bk-eyebrow det__card-title" id="det-flags">Request handling</h2>
            <ul class="det__flags">
              <li>
                <app-status
                  class="det__flag-status"
                  [text]="app.passthrough ? 'On' : 'Off'"
                  [status]="app.passthrough ? 'success' : 'disabled'"
                />
                <span>
                  <span class="det__flag-name">Passthrough</span>
                  <span class="det__flag-desc">Requests go to the callback URL as they arrive, without processing.</span>
                </span>
              </li>
              <li>
                <app-status
                  class="det__flag-status"
                  [text]="app.mnoServiceDataPassThrough ? 'On' : 'Off'"
                  [status]="app.mnoServiceDataPassThrough ? 'success' : 'disabled'"
                />
                <span>
                  <span class="det__flag-name">Network data passthrough</span>
                  <span class="det__flag-desc">Service data from the mobile network is forwarded to the callback.</span>
                </span>
              </li>
            </ul>
          </section>

          <section class="bk-panel det__card" aria-labelledby="det-imp">
            <h2 class="bk-eyebrow det__card-title" id="det-imp">Number impersonation</h2>
            @if (impersonations.length) {
              <ul class="det__imps">
                @for (imp of impersonations; track imp.id) {
                  <li class="det__imp">
                    <div class="det__imp-top">
                      <app-status [text]="imp.enabled ? 'On' : 'Off'" [status]="imp.enabled ? 'success' : 'disabled'" />
                      <span class="det__quiet">
                        {{ imp.sandboxOnly ? 'Sandbox only' : 'All environments' }}
                        @if (imp.timeToLiveDuration) {
                          , lasts {{ imp.timeToLiveDuration }} {{ (imp.durationUnit || 'minutes').toLowerCase() }}
                        }
                      </span>
                    </div>
                    @for (ctx of imp.ussdImpersonationContexts ?? []; track ctx.id) {
                      <p class="det__imp-ctx">
                        {{ ctx.originalMsisdn }} acts as {{ ctx.effectiveMsisdn }}
                        @if (ctx.reason) { <span class="det__quiet">, {{ ctx.reason }}</span> }
                        @if (ctx.expiresAt) { <span class="det__quiet">, until {{ formatWhen(ctx.expiresAt) }}</span> }
                      </p>
                    }
                  </li>
                }
              </ul>
            } @else {
              <p class="det__empty-line">No number impersonation is set up. Tests use the number they dial from.</p>
            }
          </section>

          @if (app.ussdOrganisationProfile; as profile) {
            <section class="bk-panel det__card" aria-labelledby="det-org">
              <h2 class="bk-eyebrow det__card-title" id="det-org">Organisation profile</h2>
              <dl class="bk-kv">
                <dt>Name</dt>
                <dd>{{ profile.organisationName || '—' }}</dd>
                <dt>Account</dt>
                <dd>{{ profile.accountName || '—' }}</dd>
                <dt>Reference</dt>
                <dd>{{ profile.referenceId || '—' }}</dd>
                <dt>Country</dt>
                <dd>{{ profile.countryName || '—' }}</dd>
                <dt>Status</dt>
                <dd>
                  @if (profile.status) {
                    <app-status [text]="profile.status" [status]="profile.status.toUpperCase() === 'ACTIVE' ? 'success' : profile.status" />
                  } @else {
                    —
                  }
                </dd>
              </dl>
            </section>
          }

          <section class="bk-panel det__card det__danger" aria-labelledby="det-danger">
            <div>
              <h2 class="bk-eyebrow det__card-title" id="det-danger">Delete application</h2>
              <p class="det__danger-text">
                Removes the application and frees {{ app.serviceCode || 'its code' }}. Subscribers who dial it get no menu.
              </p>
            </div>
            <button type="button" class="bk-btn bk-btn--ghost det__danger-btn" (click)="confirmDelete()">
              <span class="bk-sym" aria-hidden="true">delete</span>
              Delete application
            </button>
          </section>
        </div>
      }
    }

    <!-- ── Promote ── -->
    <app-modal-component [open]="showPromoteModal" labelledBy="det-promote-title" (dismiss)="cancelPromote()">
      <div class="promote">
        <header class="promote__head">
          <h2 class="bk-heading promote__title" id="det-promote-title">{{ promoteButtonTitle }}</h2>
          <button type="button" class="bk-icon-btn" aria-label="Close" [disabled]="isPromoting" (click)="cancelPromote()">
            <span class="bk-sym" aria-hidden="true">close</span>
          </button>
        </header>

        <p class="promote__route">
          <span>{{ environmentLabel(app.environment) }}</span>
          <span class="bk-sym" aria-hidden="true">arrow_forward</span><span class="bk-sr-only">to</span>
          <strong>{{ environmentLabel(nextStage) }}</strong>
        </p>

        <p class="promote__desc">
          @if (isPromotingToProduction) {
            A reviewer has to approve this. <strong>{{ app.name }}</strong> stays in
            {{ environmentLabel(app.environment) }} until then.
          } @else {
            <strong>{{ app.name }}</strong> moves to {{ environmentLabel(nextStage) }} straight away.
            Applications move one stage at a time.
          }
        </p>

        @if (isPromotingToProduction) {
          <div class="bk-field" [class.is-invalid]="serviceCodeTouched && !serviceCode.trim()">
            <label class="bk-field__label" for="promote-service-code">Production service code</label>
            <input
              id="promote-service-code"
              class="bk-field__input"
              type="text"
              autocomplete="off"
              placeholder="e.g. *120*555#"
              required
              aria-describedby="promote-service-code-msg"
              [attr.aria-invalid]="serviceCodeTouched && !serviceCode.trim()"
              [(ngModel)]="serviceCode"
              (blur)="serviceCodeTouched = true"
            />
            @if (serviceCodeTouched && !serviceCode.trim()) {
              <p class="bk-field__error" id="promote-service-code-msg">
                <span class="bk-sym" aria-hidden="true">error</span>
                Enter the code subscribers will dial in production.
              </p>
            } @else {
              <p class="bk-field__hint" id="promote-service-code-msg">The code subscribers will dial in production.</p>
            }
          </div>
        }

        <div class="bk-field">
          <label class="bk-field__label" for="promote-notes">Notes for the reviewer (optional)</label>
          <textarea
            id="promote-notes"
            class="bk-field__input"
            rows="3"
            placeholder="What changed and what you tested"
            [(ngModel)]="promotionNotes"
          ></textarea>
        </div>

        @if (promoteError) {
          <p class="promote__error" role="alert">
            <span class="bk-sym" aria-hidden="true">error</span>
            {{ promoteError }}
          </p>
        }

        <div class="promote__actions">
          <button type="button" class="bk-btn bk-btn--ghost" [disabled]="isPromoting" (click)="cancelPromote()">Cancel</button>
          <button
            type="button"
            class="bk-btn bk-btn--primary"
            [disabled]="isPromoteDisabled"
            [attr.aria-busy]="isPromoting || null"
            (click)="confirmPromote()"
          >
            {{ isPromoting ? (isPromotingToProduction ? 'Requesting…' : 'Promoting…') : promoteButtonTitle }}
          </button>
        </div>
      </div>
    </app-modal-component>

    <app-prompt-card
      title="Disable application"
      buttonText="Disable"
      closeText="Keep enabled"
      impact="Subscribers who dial this code get no menu until you enable it again."
      impactIcon="pause_circle"
      tone="default"
      [openModal]="showDisableConfirm"
      [loading]="isTogglingStatus"
      (onClose)="cancelDisable()"
      (onButtonClick)="confirmDisable()"
    >
      <p class="det__confirm">Disable <strong>{{ app.name }}</strong> ({{ app.serviceCode }})?</p>
    </app-prompt-card>

    <app-prompt-card
      title="Delete application"
      buttonText="Delete application"
      closeText="Keep it"
      impact="Subscribers who dial this code get no menu. This cannot be undone."
      impactIcon="warning"
      [openModal]="showDeleteConfirm"
      [loading]="isDeleting"
      (onClose)="cancelDelete()"
      (onButtonClick)="deleteApplication()"
    >
      <p class="det__confirm">Delete <strong>{{ app.name }}</strong> ({{ app.serviceCode }})?</p>
    </app-prompt-card>
  } @else if (loadErrorMsg) {
    <div class="bk-panel det__state" role="alert">
      <span class="bk-sym det__state-icon" aria-hidden="true">cloud_off</span>
      <p class="det__state-title">{{ loadErrorMsg }}</p>
      <div class="det__state-actions">
        <button type="button" class="bk-btn bk-btn--ghost" (click)="goBack()">Back to applications</button>
        <button type="button" class="bk-btn bk-btn--primary" (click)="loadApplication()">Try again</button>
      </div>
    </div>
  } @else {
    <div class="bk-panel det__state">
      <span class="bk-sym det__state-icon" aria-hidden="true">search_off</span>
      <p class="det__state-title">This application does not exist</p>
      <p class="det__state-text">It may have been deleted, or the link is wrong.</p>
      <div class="det__state-actions">
        <button type="button" class="bk-btn bk-btn--primary" (click)="goBack()">Back to applications</button>
      </div>
    </div>
  }
</div>
